/**
 * BD SDK main class and configuration.
 *
 * @remarks
 * This module contains the main BdSdk class that provides unified access
 * to all blockchain operations.
 *
 * @module BD SDK Class
 * @category Entrypoint
 * @packageDocumentation
 */
import { CantonModule } from "./modules/canton";
import { CantonApi } from "./modules/canton/canton-api";
import { ChainService } from "./modules/chains";
import { CommonModule } from "./modules/common";
import { DefiModule } from "./modules/defi";
import { DefiApi } from "./modules/defi/defi-api";
import { MetadataModule } from "./modules/metadata";
import { SecurityModule } from "./modules/security/security-module";
import { StakingModule } from "./modules/staking";
import { createStellarModule } from "./modules/stellar/stellar-module";
import { DEFAULT_BASE_PATH } from "./shared/constants";
import { getLogger, logSdkAndDependencyVersions } from "./shared/logger";
/**
 * Main BD SDK class that provides access to all blockchain functionality.
 *
 * @remarks
 * The BdSdk is the primary entry point for the Blockdaemon SDK, providing
 * unified access to all blockchain operations including DeFi, staking, chain
 * management, and common utilities. It orchestrates multiple specialized
 * modules to deliver comprehensive blockchain functionality.
 *
 * **Core Modules:**
 * - **DefiModule**: DeFi operations, swaps, and yield farming
 * - **StakingModule**: Staking and vault management
 * - **ChainService**: Multi-chain network management
 * - **CommonModule**: Shared utilities and operations
 * - **MetadataModule**: SDK health and version information
 *
 * **Backends / Upstream Integrations:**
 * - DefiModule/CommonModule primarily call Blockdaemon's Consolidated DeFi API SDK
 *   (Account/Chain/Tokens/DEX/Bridge/LendBorrow endpoints) via generated REST clients.
 *   Requests are authenticated with the provided `bdApiKey` and use `basePath` for routing.
 * - StakingModule uses the StakeWise v3 SDK to build and validate transactions, and
 *   ethers' `JsonRpcProvider` for on-chain data when necessary.
 * - ChainService constructs chain metadata and manages RPC providers for supported chains.
 *
 * **Key Features:**
 * - **Multi-chain Support**: Works across 50+ blockchain networks
 * - **Unified API**: Consistent interface across all operations
 * - **Type Safety**: Full TypeScript support with comprehensive types
 * - **Modular Architecture**: Use only the modules you need
 * - **Production Ready**: Battle-tested in production environments
 * - **Real-time Data**: Live pricing, balances, and market data
 * - **Comprehensive Logging**: Detailed logging for debugging and monitoring
 *
 * **Common Use Cases:**
 * - Building multi-chain DeFi applications
 * - Portfolio management and analytics
 * - Liquid staking integrations
 * - Cross-chain bridge applications
 * - Wallet and trading interfaces
 * - Institutional blockchain solutions
 *
 * @example
 * **Basic SDK Setup**
 * ```typescript
 * import { BdSdk } from "@blockdaemon/bd-sdk";
 *
 * const sdk = new BdSdk({
 *   bdApiKey: "your-blockdaemon-api-key",
 *   logLevel: "INFO"
 * });
 *
 * // Access different modules
 * const chains = await sdk.chains.getAllChains();
 * const balances = await sdk.common.getBalance({
 *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   chainID: "1"
 * });
 * ```
 *
 * @example
 * **Multi-chain Portfolio Management**
 * ```typescript
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY,
 *   logLevel: "DEBUG"
 * });
 *
 * // Get portfolio across multiple chains
 * const address = "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6";
 * const chains = ["1", "137", "42161"]; // Ethereum, Polygon, Arbitrum
 * const portfolio = [];
 *
 * for (const chainID of chains) {
 *   const balances = await sdk.common.getBalance({ address, chainID });
 *   portfolio.push({
 *     chainID,
 *     native: balances.data.native,
 *     tokens: balances.data.evmTokens
 *   });
 * }
 *
 * console.log("Complete portfolio:", portfolio);
 * ```
 *
 * @example
 * **DeFi Operations**
 * ```typescript
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY,
 * });
 *
 * // Get token price
 * const usdcPrice = await sdk.defi.getAssets({
 *   address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
 *   chainID: "1"
 * });
 *
 * // Get portfolio balances
 * const balances = await sdk.defi.getBalances({
 *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   chainID: "1"
 * });
 *
 * console.log(`USDC Price: $${usdcPrice.data.priceUSD}`);
 * console.log(`Portfolio tokens: ${balances.data.evmTokens.length}`);
 * ```
 *
 * @example
 * **Staking Operations**
 * ```typescript
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY,
 *   mnemonic: process.env.WALLET_MNEMONIC
 * });
 *
 * // Get staking metadata (defaults to Ethereum mainnet)
 * const info = await sdk.staking.getStakingMetadata({ chainId: "eip155:1" });
 * console.log(`APY: ${info.apyPercentage}%`);
 *
 * // Build a staking transaction
 * const stakeTx = await sdk.staking.buildStakeTx({
 *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   chainId: "eip155:1"
 * });
 * ```
 *
 * @example
 * **Using Mainnet Vault**
 * ```typescript
 * import BdSdk, { BDVaults } from "@blockdaemon/bd-sdk";
 *
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY
 * });
 *
 * // Build a staking transaction for Ethereum mainnet
 * const mainnetStakeTx = await sdk.staking.buildStakeTx({
 *   assets: BigInt("1000000000000000000"), // 1 ETH in wei
 *   userAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   vaultAddress: BDVaults.ethereum // Blockdaemon mainnet vault
 * });
 * ```
 *
 * @example
 * **IChain Management**
 * ```typescript
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY
 * });
 *
 * // Get all supported chains
 * const allChains = await sdk.chains.getAllChains();
 * console.log(`Supported chains: ${allChains.length}`);
 *
 * // Get mainnet chains only
 * const mainnetChains = sdk.chains.getMainnetChains();
 * console.log("Mainnet chains:", mainnetChains.map(c => c.chainName));
 *
 * // Get specific chain details
 * const ethereum = sdk.chains.getChain("1");
 * if (ethereum) {
 *   console.log(`RPC URLs: ${ethereum.rpc.length}`);
 *   console.log(`Block explorer: ${ethereum.blockExplorerUrls?.[0]}`);
 * }
 * ```
 *
 * @example
 * **Error Handling and Monitoring**
 * ```typescript
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY,
 *   logLevel: "DEBUG" // Enable detailed logging
 * });
 *
 * try {
 *   // Check SDK health
 *   const version = sdk.metadata.getVersion();
 *   console.log(`SDK Version: ${version}`);
 *
 *   // Perform operations with error handling
 *   const balances = await sdk.common.getBalance({
 *     address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *     chainID: "1"
 *   });
 *
 *   console.log("Operation successful:", balances.data.evmTokens.length);
 * } catch (error) {
 *   console.error("SDK operation failed:", error);
 * }
 * ```
 *
 * @example
 * **Custom Endpoint Configuration**
 * ```typescript
 * // Override with a custom proxy or self-hosted endpoint
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY,
 *   logLevel: "INFO",
 *   basePath: process.env.BD_SDK_BASE_PATH ?? "https://svc.blockdaemon.com"
 * });
 *
 * const chains = sdk.chains.getMainnetChains();
 * console.log(`Using ${chains.length} mainnet chains`);
 * ```
 *
 * @example
 * **Custom Logger Integration**
 * ```typescript
 * import { getLogger } from "@blockdaemon/bd-sdk";
 *
 * const customLogger = getLogger({
 *   level: "INFO",
 *   label: "my-app-bd-sdk"
 * });
 *
 * const sdk = new BdSdk({
 *   bdApiKey: process.env.BD_API_KEY!,
 *   logLevel: "INFO"
 * });
 *
 * // Logger is automatically configured and shared across all modules
 * ```
 *
 *
 */
export class BdSdk {
    /** Internal configuration object */
    configuration;
    /** Base URL for API endpoints */
    basePath;
    /** Internal logger instance */
    logger;
    /** DeFi operations module */
    defi;
    /** Chain management and information module */
    chains;
    /** Staking operations module */
    staking;
    /** Common blockchain operations module */
    common;
    /** Metadata and token information module */
    metadata;
    /** Stellar transaction building module */
    stellar;
    /** Security module for transaction verification */
    security;
    /** Canton DTCC transaction services module */
    canton;
    /**
     * Creates a new BD SDK instance with comprehensive blockchain functionality.
     *
     * @remarks
     * The constructor initializes all SDK modules and establishes connections
     * to Blockdaemon services. It validates the provided configuration and
     * sets up logging, authentication, and module dependencies.
     *
     * **Initialization Process:**
     * 1. Validates required API keys and configuration
     * 2. Sets up logging infrastructure
     * 3. Initializes all SDK modules with shared configuration
     * 4. Establishes connections to Blockdaemon services
     * 5. Loads supported chain configurations
     *
     * **Module Dependencies:**
     * - All modules share the same logger instance
     * - API keys are distributed to all modules
     * - Configuration is propagated throughout the SDK
     *
     * @param options - Configuration options for the SDK
     * @throws {Error} When BD API key is not provided or is empty
     * @throws {Error} When the configuration is invalid
     *
     * @example
     * **Basic Initialization**
     * ```typescript
     * const sdk = new BdSdk({
     *   bdApiKey: "your-blockdaemon-api-key"
     * });
     * ```
     *
     * @example
     * **Full Configuration**
     * ```typescript
     * const sdk = new BdSdk({
     *   bdApiKey: "your-blockdaemon-api-key",
     *   logLevel: "DEBUG",
     *   basePath: "https://svc.blockdaemon.com",
     *   mnemonic: "your wallet mnemonic for transactions"
     * });
     * ```
     *
     * @example
     * **Environment-based Configuration**
     * ```typescript
     * const sdk = new BdSdk({
     *   bdApiKey: process.env.BD_API_KEY!,
     *   logLevel: (process.env.LOG_LEVEL as LogLevelDesc) || "INFO",
     *   basePath: process.env.BD_BASE_PATH
     * });
     * ```
     *
     * @example
     * **Custom Endpoint**
     * ```typescript
     * const sdk = new BdSdk({
     *   bdApiKey: process.env.BD_API_KEY!,
     *   logLevel: "WARN",
     *   basePath: process.env.BD_SDK_BASE_PATH ?? "https://svc.blockdaemon.com"
     * });
     * ```
     *
     * @example
     * **Custom Logger Integration**
     * ```typescript
     * import { getLogger } from "@blockdaemon/bd-sdk";
     *
     * const customLogger = getLogger({
     *   level: "INFO",
     *   label: "my-app-bd-sdk"
     * });
     *
     * const sdk = new BdSdk({
     *   bdApiKey: process.env.BD_API_KEY!,
     *   logLevel: "INFO"
     * });
     *
     * // Logger is automatically configured and shared across all modules
     * ```
     *
     *
     */
    constructor({ configuration, basePath = DEFAULT_BASE_PATH, bdApiKey, logLevel = "INFO", chainRetryOptions, rpcBasePath, canton: cantonOptions, }) {
        // Initialize logger first so we can emit warnings even if bdApiKey is missing
        this.logger = getLogger({ label: "bd-sdk", level: logLevel });
        // Emit SDK and dependency version information at init
        logSdkAndDependencyVersions(this.logger);
        if (!bdApiKey || bdApiKey.trim() === "") {
            this.logger.warn("BD API key is not set. Some API calls may fail with 401 Unauthorized. Provide BD_API_KEY to enable authenticated features.");
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = configuration.basePath || basePath;
        }
        else {
            this.basePath = basePath;
        }
        // Resolve RPC base path from constructor (falls back to basePath)
        const effectiveRpcBasePath = rpcBasePath && rpcBasePath.trim() !== "" ? rpcBasePath : this.basePath;
        const chainServiceConfig = {
            bdApiKey,
            logger: this.logger,
            retryOptions: chainRetryOptions,
            rpcBasePath: effectiveRpcBasePath,
        };
        this.chains = new ChainService(chainServiceConfig);
        const securityConfig = {
            logger: this.logger,
            logLevel,
        };
        this.security = new SecurityModule(securityConfig);
        const defiApi = new DefiApi({ bdApiKey, basePath: this.basePath });
        const defiConfig = {
            defiApi,
            logger: this.logger,
        };
        this.defi = new DefiModule(defiConfig);
        const stakingConfig = {
            bdApiKey,
            logger: this.logger,
            basePath: this.basePath, // Pass basePath as fallback
            rpcBasePath: effectiveRpcBasePath,
            security: this.security,
            referralTag: this.configuration?.referralTag,
            protocolConfig: this.configuration?.protocolConfig,
        };
        this.staking = new StakingModule(stakingConfig);
        const commonConfig = {
            chainService: this.chains,
            bdApiKey,
            logger: this.logger,
            basePath: this.basePath,
        };
        this.common = new CommonModule(commonConfig);
        const metadataConfig = {
            bdApiKey,
            logger: this.logger,
        };
        this.metadata = new MetadataModule(metadataConfig);
        this.stellar = createStellarModule({
            bdApiKey: bdApiKey || "",
            rpcBasePath: effectiveRpcBasePath,
        });
        const cantonApi = new CantonApi({
            bdApiKey,
            basePath: this.basePath,
            ...(cantonOptions?.network ? { network: cantonOptions.network } : {}),
        });
        const cantonConfig = {
            cantonApi,
            logger: this.logger,
        };
        this.canton = new CantonModule(cantonConfig);
    }
}
//# sourceMappingURL=data:application/json;base64,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